import Tippy from '@tippyjs/react';
import { ButtonHTMLAttributes, ReactNode, forwardRef, useMemo, useRef } from 'react';
import { Instance } from 'tippy.js';

import { InitLoader, Text } from '@components';
import { VariantType } from '@enums';
import { cn, colors } from '@utils';

export interface BaseButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  variant?: VariantType;
  withoutHover?: boolean;
  withoutScale?: boolean;
  hoverEffect?: boolean;
  loaderColor?: string;
  hint?: ReactNode;
  hintPlacement?: 'top' | 'bottom' | 'left' | 'right';
  predefinedColor?: keyof typeof colors;
  adornment?: ReactNode;
  direction?: 'left' | 'right';
}

export const BaseButton = forwardRef<HTMLButtonElement, BaseButtonProps>(
  (
    {
      type = 'button',
      disabled = false,
      loading = false,
      variant = VariantType.Contained,
      className = '',
      children,
      hoverEffect = false,
      withoutScale = false,
      hint,
      hintPlacement = 'top',
      predefinedColor = '',
      loaderColor = '#CCCCCC',
      adornment,
      direction = 'left',
      onClick,
      ...props
    },
    ref,
  ) => {
    const tippyInstance = useRef<Instance | null>(null);
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      tippyInstance.current?.hide();
      onClick?.(e);
    };

    const dynamicClasses = useMemo(
      () =>
        cn(
          predefinedColor && colors[predefinedColor],
          'flex gap-3 items-center justify-center cursor-pointer enabled:shadow-button',
          'focus:outline-none outline-none',
          'text-us ellipsis-text',
          disabled || loading
            ? 'cursor-not-allowed bg-opacity-80 text-opacity-40 text-gray-400 bg-black/5'
            : hoverEffect
              ? 'hover:bg-black/5 transition-all duration-500 ease-in-out'
              : !withoutScale && 'hover:opacity-85 hover:scale-95',
          className,
        ),
      [className, disabled, hoverEffect, loading, withoutScale, predefinedColor, loaderColor],
    );

    const buttonElement = (
      <button
        ref={ref}
        type={type}
        className={dynamicClasses}
        disabled={disabled || loading}
        {...((disabled || loading) && { 'aria-disabled': true })}
        {...(loading && { 'aria-busy': true })}
        onClick={handleClick}
        {...props}
      >
        <>
          {loading && <InitLoader size={15} dot={6} color={loaderColor} />}
          {adornment && direction === 'left' && !loading && adornment}
          {typeof children === 'string' ? (
            <Text $level={6} $customizeColor className="truncate">
              {children}
            </Text>
          ) : (
            children
          )}
          {adornment && direction === 'right' && !loading && adornment}
        </>
      </button>
    );
    if (!hint) return buttonElement;

    return (
      <Tippy
        disabled={!hint}
        placement={hintPlacement}
        content={hint}
        animation="shift-away"
        arrow
        theme="custom"
        className="text-white font-normal px-1 py-0.5"
        duration={[200, 0]}
        offset={[0, 10]}
        delay={[75, 0]}
        appendTo={() => document.body}
        trigger="mouseenter"
        onCreate={(instance) => {
          tippyInstance.current = instance;
        }}
      >
        {buttonElement}
      </Tippy>
    );
  },
);
